import React from 'react';
import { Package, Clock, MapPin, Tag } from 'lucide-react';

const OrderSummary = ({
  orderData,
  subtotal,
  tax,
  discount,
  total,
  promoCode,
  onPromoCodeChange,
  onApplyPromo,
  paymentService
}) => {
  if (!orderData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h3>
      {/* Order Items */}
      <div className="space-y-4 mb-6">
        {orderData?.order_items?.map((item) => (
          <div key={item?.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-900">
                {item?.services?.name || item?.products?.name || 'Service/Product'}
              </h4>
              {item?.services?.category && (
                <p className="text-xs text-gray-500 capitalize">
                  {item?.services?.category?.replace('_', ' ')}
                </p>
              )}
              {item?.services?.duration_hours && (
                <div className="flex items-center mt-1">
                  <Clock className="h-3 w-3 text-gray-400 mr-1" />
                  <span className="text-xs text-gray-500">
                    {item?.services?.duration_hours}h duration
                  </span>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">Qty: {item?.quantity}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">
                {paymentService?.formatCurrency(item?.total_price)}
              </p>
              {item?.quantity > 1 && (
                <p className="text-xs text-gray-500">
                  {paymentService?.formatCurrency(item?.unit_price)} each
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Service Details */}
      {orderData?.service_address && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <MapPin className="h-4 w-4 text-blue-600 mt-1" />
            <div>
              <p className="text-sm font-medium text-blue-900">Service Address:</p>
              <p className="text-sm text-blue-700">{orderData?.service_address}</p>
            </div>
          </div>
          {orderData?.service_date && (
            <div className="flex items-center space-x-2 mt-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Service Date:</p>
                <p className="text-sm text-blue-700">
                  {new Date(orderData.service_date)?.toLocaleDateString('en-ZA', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Promo Code */}
      <div className="mb-6">
        <div className="flex space-x-2">
          <input
            type="text"
            placeholder="Promo code"
            value={promoCode}
            onChange={(e) => onPromoCodeChange(e?.target?.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={onApplyPromo}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
          >
            <Tag className="h-4 w-4 mr-1" />
            Apply
          </button>
        </div>
        {discount > 0 && (
          <p className="text-sm text-green-600 mt-1">
            {discount}% discount applied!
          </p>
        )}
      </div>
      {/* Price Breakdown */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Subtotal</span>
          <span className="text-gray-900">{paymentService?.formatCurrency(subtotal)}</span>
        </div>
        
        {discount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Discount ({discount}%)</span>
            <span>-{paymentService?.formatCurrency(discount)}</span>
          </div>
        )}
        
        <div className="flex justify-between">
          <span className="text-gray-600">VAT (15%)</span>
          <span className="text-gray-900">{paymentService?.formatCurrency(tax)}</span>
        </div>
        
        <hr className="my-2" />
        
        <div className="flex justify-between text-lg font-semibold">
          <span className="text-gray-900">Total</span>
          <span className="text-gray-900">{paymentService?.formatCurrency(total)}</span>
        </div>
      </div>
      {/* Customer Info */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Customer Information</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>{orderData?.customer_name}</p>
          <p>{orderData?.customer_email}</p>
          {orderData?.customer_phone && <p>{orderData?.customer_phone}</p>}
        </div>
      </div>
      {/* Order Notes */}
      {orderData?.notes && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Special Notes</h4>
          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">{orderData?.notes}</p>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;